import { useSelector } from 'react-redux'
import { useCurrentPractice } from 'contexts/PracticeContext'
import { selectContext } from 'selectors/auth'
import { isManagerOrAdmin } from 'util/unified'

interface CapitalAccess {
	isCapitalEnabled: boolean
	hasAllowedRole: boolean
	canAccess: boolean
}

/**
 * Centralizes the visibility gate for Stripe Capital surfaces (route guard,
 * sidebar promo tile, etc.).
 *
 * `isCapitalEnabled` reads `consolidatedPractice.settings.isCapitalEnabled` to
 * mirror the backend middleware. Checking the child practice's settings would
 * allow Capital UI on practices the server still rejects (403), and the global
 * axios interceptor would treat that as an auth failure and force-logout.
 *
 * `hasAllowedRole` delegates to `isManagerOrAdmin`, which already covers HM
 * (Firestore + Postgres `user.practices`) and admin/regional-manager paths.
 */
export const useCapitalAccess = (): CapitalAccess => {
	const { currentPracticeObject } = useCurrentPractice()
	const context = useSelector(selectContext)

	const isCapitalEnabled =
		currentPracticeObject?.consolidatedPractice?.settings?.isCapitalEnabled === true
	const hasAllowedRole = isManagerOrAdmin(context, currentPracticeObject)

	return {
		isCapitalEnabled,
		hasAllowedRole,
		canAccess: isCapitalEnabled && hasAllowedRole
	}
}
